// Pipeline board: chip projects as cards on phase lanes, staffing allocation,
// tape-out/respin decisions, and the start-project form (which is also where
// the analog/digital path gets locked — design §5).

import { useState } from "react";
import type { Command, Discipline, ViewProject } from "../../engine/types.js";
import { DISCIPLINES } from "../../engine/types.js";
import { dieArea, unitCost } from "../../engine/formulas/yieldModel.js";
import { idealStaffing, planProjectEffort } from "../../engine/formulas/effort.js";
import { interpolate } from "../../engine/calendar.js";
import { archFitFactor } from "../../engine/systems/market.js";
import { activeTreeOf, estimatePWorks, estimateRework, expectedKGatesAt, marketColor, refAspAt, viewCtx, type ScreenProps } from "./shared.js";
import { projectNextMonth } from "../allocation.js";
import { autoStaffCommands, effectivePhase } from "../autoStaff.js";
import { money, num, pct } from "../format.js";
import { Bar, DieRect, Stepper } from "../components/widgets.js";

const LANES: { title: string; phases: ViewProject["phase"][] }[] = [
  { title: "FEASIBILITY", phases: ["feasibility"] },
  { title: "DESIGN", phases: ["design"] },
  { title: "AWAITING TAPE-IN", phases: ["waitingTapein"] },
  { title: "LAYOUT", phases: ["layout"] },
  { title: "AWAITING TAPE-OUT", phases: ["waitingTapeout"] },
  { title: "IN FAB", phases: ["fab"] },
  { title: "VALIDATION / RESPIN", phases: ["validation", "respinDecision", "respinFix"] },
  { title: "PRODUCTION QUAL", phases: ["prodQual"] },
];

export function Pipeline({ snap, content, session }: ScreenProps) {
  const { view } = snap;
  const live = view.projects.filter((p) => !["shipped", "cancelled"].includes(p.phase));
  // queued startProject orders render as ghost cards until the turn ends
  const queuedStarts = snap.pending
    .map((c, i) => ({ c, i }))
    .filter((x): x is { c: Extract<Command, { type: "startProject" }>; i: number } => x.c.type === "startProject");

  return (
    <div>
      <h2>PIPELINE BOARD</h2>
      <PoolSummary snap={snap} content={content} session={session} />
      <StartProject snap={snap} content={content} session={session} />
      <div className="lanes pipeline-lanes">
        {LANES.map((lane) => (
          <div className="lane" key={lane.title}>
            <div className="lane-title">
              {lane.title} ({live.filter((p) => lane.phases.includes(p.phase)).length}
              {lane.title === "FEASIBILITY" && queuedStarts.length > 0 ? ` +${queuedStarts.length} queued` : ""})
            </div>
            {lane.title === "FEASIBILITY" &&
              queuedStarts.map(({ c, i }) => (
                <div className="card ghost" key={`queued-${i}`}>
                  <div className="head">
                    <span className="id">QUEUED ORDER</span>
                    <span className="dim">
                      {content.nodes.find((n) => n.id === c.nodeId)?.displayName} · {num(c.kGates)}kG
                    </span>
                  </div>
                  <div className="meta">{content.markets.find((mk) => mk.id === c.marketId)?.displayName}</div>
                  <div className="warn-text">Starts when you END TURN ▸</div>
                  <div className="actions">
                    <button className="act bad" onClick={() => session.unqueue(i)}>
                      ✗ cancel order
                    </button>
                  </div>
                </div>
              ))}
            {live
              .filter((p) => lane.phases.includes(p.phase))
              .map((p) => (
                <ProjectCard key={p.id} project={p} snap={snap} content={content} session={session} />
              ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function PoolSummary({ snap, content, session }: ScreenProps) {
  const { view, pending, report } = snap;
  const proj = projectNextMonth(view, pending, content);
  const autoCmds = autoStaffCommands(view, pending, content);
  return (
    <div className="panel">
      <div className="title">ENGINEERING POOLS — next month's commitments, queued orders included</div>
      <div className="row">
        {DISCIPLINES.map((d) => {
          const p = proj[d];
          const over = p.idle < -1e-9;
          const alloc = report?.allocation[d];
          return (
            <div key={d} className="tile">
              <div className="k">{d.toUpperCase()}</div>
              <div className={`v${over ? " bad" : p.idle < 0.5 ? " warn" : ""}`}>
                {p.committed % 1 === 0 ? p.committed : p.committed.toFixed(1)} / {p.budget % 1 === 0 ? p.budget : p.budget.toFixed(1)}
                <span className="sub"> {over ? ` OVER by ${(-p.idle).toFixed(1)}` : ` · ${p.idle.toFixed(1)} idle`}</span>
              </div>
              <div className="sub">
                nre {p.nre.toFixed(0)} · sustain {p.sustaining.toFixed(1)} · qual {p.qual.toFixed(1)} · yield {p.research.toFixed(0)} · rel{" "}
                {p.reliability.toFixed(0)} · tools {p.tools.toFixed(0)}
                {p.tree > 0 ? ` · tree ${p.tree.toFixed(1)}` : ""} · projects {p.projects.toFixed(0)}
              </div>
              {alloc && <div className="sub dim">last month: {(alloc.budget - alloc.idle).toFixed(1)} used, {alloc.idle.toFixed(1)} idle</div>}
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 6, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <button
          className="act"
          disabled={autoCmds.length === 0}
          title="Queues staffing orders — inspect them on the cards and adjust before END TURN"
          onClick={() => autoCmds.forEach((c) => session.queue(c))}
        >
          {autoCmds.length > 0 ? `⚙ AUTO-STAFF — ${autoCmds.length} project${autoCmds.length === 1 ? "" : "s"}` : "⚙ AUTO-STAFF — at plan"}
        </button>
        <span className="hint">
          qual crews first (hard {content.economy.models.prodQualMinImpl}-engineer gate), then the whole pool splits in proportion to ideal
          teams — surplus overstaffs past ideal (diminishing returns beat idle payroll)
        </span>
      </div>
      <span className="hint">
        Over-committing wastes nothing — the engine draws in priority order (NRE → sustaining → node programs → projects, oldest first) and
        whoever is last gets starved. The strip at the bottom of the screen shows this ledger everywhere. Ramping hires count at 60%.
      </span>
    </div>
  );
}

function ProjectCard({ project: p, snap, content, session }: ScreenProps & { project: ViewProject }) {
  const { view, pending } = snap;
  const models = content.economy.models;
  const node = content.nodes.find((n) => n.id === p.nodeId)!;
  const market = content.markets.find((m) => m.id === p.marketId)!;
  const pWorks = estimatePWorks(p, content);
  const nodeQualified = view.nodes[p.nodeId]!.status === "qualified";
  const queuedStaffing = pending.find((c) => c.type === "setStaffing" && c.projectId === p.id);
  const staffingShown = (d: Discipline): { cur: number; queued?: number } => ({
    cur: p.staffing[d],
    queued: queuedStaffing && queuedStaffing.type === "setStaffing" ? queuedStaffing.staffing[d] : undefined,
  });
  const setStaff = (d: Discipline, v: number) => {
    const base: Record<Discipline, number> = {
      design: staffingShown("design").queued ?? p.staffing.design,
      verification: staffingShown("verification").queued ?? p.staffing.verification,
      implementation: staffingShown("implementation").queued ?? p.staffing.implementation,
    };
    base[d] = v;
    session.queue({ type: "setStaffing", projectId: p.id, staffing: base });
  };
  // ≤1 decision pending per project (session replace-key); clicking a queued
  // decision un-queues it. Steppers and ideals follow the phase the project is
  // HEADED to with that decision applied — so TAPE IN surfaces the design dial
  // (layout support), and TAPE OUT releases the whole card (team disbands).
  const queuedDecisionIdx = pending.findIndex(
    (c) => (c.type === "tapeIn" || c.type === "tapeOut" || c.type === "respin" || c.type === "cancelProject") && c.projectId === p.id,
  );
  const queuedDecision = queuedDecisionIdx >= 0 ? pending[queuedDecisionIdx]!.type : null;
  const toggleDecision = (cmd: Extract<Command, { type: "tapeIn" | "tapeOut" | "respin" | "cancelProject" }>) => {
    if (queuedDecision === cmd.type) session.unqueue(queuedDecisionIdx);
    else session.queue(cmd);
  };
  const effPhase = effectivePhase(p, pending);
  const ideals = idealStaffing({ ...p, phase: effPhase }, models);

  return (
    <div className="card">
      <div className="head">
        <span className="id">{p.id}</span>
        <span className="dim">
          {node.displayName} · {num(p.kGates)}kG · {Math.ceil(p.blocks)} blk{p.offPath ? " · OFF-PATH" : ""}
        </span>
      </div>
      <div className="meta">
        {market.displayName}
        {p.requirementsFit !== null && <> · fit {p.requirementsFit.toFixed(2)}</>}
        {p.respins > 0 && <> · respin #{p.respins}</>}
        {p.errataCount > 0 && <> · {p.errataCount} errata</>}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, margin: "2px 0 6px" }}>
        <DieRect dieAreaMm2={p.dieAreaMm2 ?? dieArea(p.kGates, node, models)} color={marketColor(p.marketId)} ghost={p.dieAreaMm2 === null} />
        <span className="dim">{p.dieAreaMm2 !== null ? `${p.dieAreaMm2.toFixed(0)}mm²` : `~${dieArea(p.kGates, node, models).toFixed(0)}mm² est.`}</span>
      </div>

      {p.phase === "feasibility" && (
        <>
          <Bar frac={1 - p.feasMonthsLeft / models.feasMonths} label="feasibility" right={`${p.feasMonthsLeft}mo left`} />
          <div className="hint">design EM buys requirements-fit ({p.feasEmDone.toFixed(1)} EM so far)</div>
        </>
      )}

      {(p.phase === "design" || p.phase === "waitingTapein") && (
        <>
          <Bar frac={p.designEmDone / p.designEmTotal} label="design" right={`${p.designEmDone.toFixed(0)}/${p.designEmTotal.toFixed(0)} EM`} />
          <Bar
            frac={p.implEmDone / p.implEmTotal}
            label="early physical"
            right={`${p.implEmDone.toFixed(0)}/${p.implEmTotal.toFixed(0)} EM (cap ${(models.implEarlyCapFrac * 100).toFixed(0)}%)`}
          />
          <Bar frac={p.coverage} amber label="coverage confidence" right={pct(p.coverage)} />
          {p.rtlReworks > 0 && <div className="bad-text">RTL rework #{p.rtlReworks} — bug found in layout, redoing design</div>}
        </>
      )}

      {p.phase === "waitingTapein" && (
        <>
          <div className="hint">
            tape in at this coverage → P(layout finds a killer RTL bug) ≈ {pct(estimateRework(p, content, p.coverage))} (back to design)
          </div>
          <div className="actions">
            <button
              className={`act warn${queuedDecision === "tapeIn" ? " queued" : ""}`}
              disabled={p.implEmDone < models.tapeinMinImplFrac * p.implEmTotal}
              title={queuedDecision === "tapeIn" ? "click to un-queue" : undefined}
              onClick={() => toggleDecision({ type: "tapeIn", projectId: p.id })}
            >
              {queuedDecision === "tapeIn" ? "TAPE-IN QUEUED ✕" : "TAPE IN — freeze netlist, start layout"}
            </button>
            {p.implEmDone < models.tapeinMinImplFrac * p.implEmTotal && (
              <span className="hint">
                floorplan not ready — needs {(models.tapeinMinImplFrac * p.implEmTotal).toFixed(1)} implementation EM
              </span>
            )}
          </div>
        </>
      )}

      {(p.phase === "layout" || p.phase === "waitingTapeout") && (
        <>
          <Bar frac={p.implEmDone / p.implEmTotal} label="layout" right={`${p.implEmDone.toFixed(0)}/${p.implEmTotal.toFixed(0)} EM`} />
          <Bar frac={p.coverage} amber label="coverage confidence" right={pct(p.coverage)} />
          <div className="hint">P(first silicon works) ≈ {pct(pWorks)} — honest but uncertain</div>
          {p.phase === "layout" && p.covAtTapein !== null && (
            <div className="hint">
              taped in at {pct(p.covAtTapein)} coverage → residual P(back to design) ≈ {pct(estimateRework(p, content, p.covAtTapein))}
              {p.staffing.design === 0 && ideals.design > 0 && (
                <span className="warn-text"> · no design support: layout at {pct(models.layoutNoDesignEff, 0)} speed</span>
              )}
            </div>
          )}
        </>
      )}

      {p.phase === "waitingTapeout" && (
        <div className="actions">
          <button
            className={`act warn${queuedDecision === "tapeOut" ? " queued" : ""}`}
            disabled={!nodeQualified}
            title={queuedDecision === "tapeOut" ? "click to un-queue" : undefined}
            onClick={() => toggleDecision({ type: "tapeOut", projectId: p.id })}
          >
            {queuedDecision === "tapeOut" ? "TAPE-OUT QUEUED ✕" : `TAPE OUT — masks ${money(node.maskSetCost)}`}
          </button>
          {queuedDecision === "tapeOut" && <span className="hint">team released at tape-out — staffing clears on END TURN</span>}
          {!nodeQualified && <span className="hint">node still qualifying</span>}
        </div>
      )}

      {p.phase === "fab" && <Bar frac={1 - p.fabMonthsLeft / node.fabCycleMonths} label="wafers in fab" right={`${p.fabMonthsLeft}mo`} />}

      {p.phase === "validation" && <div className="warn-text">parts back — bring-up in progress…</div>}

      {p.phase === "respinDecision" && (
        <>
          <div className="bad-text">first silicon DEAD ON ARRIVAL</div>
          <div className="hint">
            respin: fix bugs, re-tape at {money(node.maskSetCost * models.respinMaskFactor)}; silicon debug raises coverage
          </div>
          <div className="actions">
            <button className={`act warn${queuedDecision === "respin" ? " queued" : ""}`} onClick={() => toggleDecision({ type: "respin", projectId: p.id })}>
              {queuedDecision === "respin" ? "RESPIN QUEUED ✕" : "RESPIN"}
            </button>
            <button
              className={`act bad${queuedDecision === "cancelProject" ? " queued" : ""}`}
              onClick={() => toggleDecision({ type: "cancelProject", projectId: p.id })}
            >
              {queuedDecision === "cancelProject" ? "CANCEL QUEUED ✕" : "CANCEL PROJECT"}
            </button>
          </div>
        </>
      )}

      {p.phase === "respinFix" && (
        <>
          <Bar frac={1 - p.respinFixEmRemaining / models.respinFixEm} label="bug fix" right={`${p.respinFixEmRemaining.toFixed(1)} EM left`} />
          {p.respinFixStalled && <div className="bad-text">stalled — allocate designers</div>}
        </>
      )}

      {p.phase === "prodQual" && (
        <>
          <Bar frac={1 - p.prodQualMonthsLeft / (models.prodQualMonths + 1)} label="production qual" right={`${p.prodQualMonthsLeft}mo`} />
          {p.prodQualStalled && <div className="bad-text">STALLED — needs {models.prodQualMinImpl} implementation engineers</div>}
        </>
      )}

      {!["fab", "validation", "cancelled"].includes(effPhase) && (
        <>
        <div className="scroll-x">
          <table style={{ marginTop: 6 }}>
            <tbody>
              {DISCIPLINES.map((d) => {
                const relevant =
                  (effPhase === "feasibility" && d === "design") ||
                  effPhase === "design" ||
                  effPhase === "layout" ||
                  effPhase === "waitingTapein" || // design stays selectable: it becomes the layout-support crew at tape-in
                  (effPhase === "waitingTapeout" && d === "verification") ||
                  (effPhase === "respinFix" && d === "design") ||
                  (effPhase === "respinDecision" && d === "design") ||
                  (effPhase === "prodQual" && d === "implementation");
                if (!relevant) return null;
                const s = staffingShown(d);
                // prodQual is a GATE, not a dial: the engine draws exactly prodQualMinImpl
                // EM/mo once staffed ≥ that — so the dial is capped there (values above
                // are meaningless; a bigger inherited number clamps once touched)
                const fixedDraw = effPhase === "prodQual" && d === "implementation";
                const ideal = ideals[d];
                const shown = s.queued ?? s.cur;
                const overIdeal = ideal > 0 && shown > 1.5 * ideal;
                const starveAt = models.starveKnee * (1 - 1 / Math.max(1, p.blocks)) * ideal;
                const starved = ideal > 0 && shown > 0 && shown < starveAt;
                return (
                  <tr key={d}>
                    <td title="Ideal team ≈ one owner per block — below the knee the crew thrashes across blocks; beyond ~1.5× ideal, extra heads have no block to own">
                      {d.slice(0, 6)}
                      {!fixedDraw && ideal > 0 && (
                        <div className={starved || overIdeal ? "warn-text" : "dim"} style={{ fontSize: 10 }}>
                          {starved ? "⚠ starved · " : overIdeal ? "⚠ " : ""}ideal ~{ideal < 10 ? ideal.toFixed(1) : Math.round(ideal)}
                        </div>
                      )}
                      {effPhase === "waitingTapein" && d === "design" && (
                        <div className="dim" style={{ fontSize: 10 }}>
                          idle until tape-in
                        </div>
                      )}
                    </td>
                    <td className="r">
                      <Stepper
                        value={s.cur}
                        queued={s.queued}
                        max={fixedDraw ? models.prodQualMinImpl : 9999}
                        onChange={(v) => setStaff(d, v)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {p.phase === "prodQual" &&
          ((staffingShown("implementation").queued ?? p.staffing.implementation) >= models.prodQualMinImpl ? (
            <div className="hint">test & characterization crew — fixed {models.prodQualMinImpl} EM/mo</div>
          ) : (
            <div className="bad-text" style={{ fontSize: 11 }}>0 drawn — qual stalls below {models.prodQualMinImpl} engineers</div>
          ))}
        </>
      )}

      {["feasibility", "design", "waitingTapein", "layout"].includes(p.phase) && (
        <div className="actions">
          <button
            className={`act bad${queuedDecision === "cancelProject" ? " queued" : ""}`}
            onClick={() => toggleDecision({ type: "cancelProject", projectId: p.id })}
          >
            {queuedDecision === "cancelProject" ? "CANCEL QUEUED ✕" : "cancel"}
          </button>
        </div>
      )}
    </div>
  );
}

function StartProject({ snap, content, session }: ScreenProps) {
  const { view } = snap;
  const models = content.economy.models;
  const queuedStartCount = snap.pending.filter((c) => c.type === "startProject").length;
  const ownedNodes = content.nodes.filter((n) => view.nodes[n.id]!.status !== "locked");
  const [marketId, setMarketId] = useState(content.markets[0]!.id);
  const [nodeId, setNodeId] = useState(ownedNodes[ownedNodes.length - 1]?.id ?? content.nodes[0]!.id);
  // raw text so the field can be cleared while typing; clamp only when read
  const [kGatesText, setKGatesText] = useState("20");
  const kGates = Math.max(1, Math.round(Number(kGatesText) || 1));
  const [ipIds, setIpIds] = useState<string[]>([]);
  // architecture (P1): researched tree nodes snapshotted into the chip — default
  // everything you hold; the preview shows what each segment thinks of the set
  const tree = activeTreeOf(content);
  const researched = tree ? tree.nodes.filter((n) => n.id in view.tree.researched) : [];
  const [excluded, setExcluded] = useState<string[]>([]);
  const archIds = researched.map((n) => n.id).filter((id) => !excluded.includes(id));

  const market = content.markets.find((m) => m.id === marketId)!;
  const node = content.nodes.find((n) => n.id === nodeId)!;
  const nodeOwned = view.nodes[nodeId] && view.nodes[nodeId]!.status !== "locked";
  const m = view.monthIndex;
  const mRel = m + 18; // typical founding-to-release span under the tape-in pipeline
  const area = dieArea(kGates, node, models);
  const uc = unitCost(node, market, area, mRel, view.nodes[nodeId]?.researchEm ?? 0, models);
  const expected = expectedKGatesAt(market, mRel);
  // licensed IP (M3): effort/λ run on the effective gates; die and market credit stay full
  const availableIp = content.ip.filter((ip) => ip.availableFrom <= m);
  const chosenIp = availableIp.filter((ip) => ipIds.includes(ip.id));
  const ipFrac = Math.min(0.5, chosenIp.reduce((a, ip) => a + ip.kGatesFrac, 0));
  const ipUpfront = chosenIp.reduce((a, ip) => a + interpolate(ip.upfront, m), 0);
  const ipRoyalty = chosenIp.reduce((a, ip) => a + ip.royaltyPct, 0);
  const kEff = Math.max(1, Math.round(kGates * (1 - ipFrac)));
  const edaMult = content.economy.tools.edaTiers[view.tools.edaTier].methodMult;
  const plan = planProjectEffort(
    kEff,
    m,
    models,
    market.pathAffinity !== "neutral" && view.path !== null && market.pathAffinity !== view.path,
    edaMult,
  );
  const locksPath = view.path === null && market.pathAffinity !== "neutral";
  const offPath = view.path !== null && market.pathAffinity !== "neutral" && market.pathAffinity !== view.path;

  return (
    <div className="panel">
      <div className="title">START A CHIP PROJECT</div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <select value={marketId} onChange={(e) => setMarketId(e.target.value)}>
          {content.markets.map((mk) => (
            <option key={mk.id} value={mk.id}>
              {mk.displayName} [{mk.pathAffinity}]
            </option>
          ))}
        </select>
        <select value={nodeId} onChange={(e) => setNodeId(e.target.value)}>
          {ownedNodes.map((n) => (
            <option key={n.id} value={n.id}>
              {n.displayName} ({view.nodes[n.id]!.status})
            </option>
          ))}
        </select>
        <label>
          kGates{" "}
          <input
            type="number"
            min={1}
            value={kGatesText}
            onChange={(e) => setKGatesText(e.target.value)}
            onBlur={() => setKGatesText(String(kGates))}
          />
        </label>
        <button
          className="act warn"
          disabled={!nodeOwned}
          onClick={() =>
            session.queue({
              type: "startProject",
              marketId,
              nodeId,
              kGates,
              ...(ipIds.length ? { ipIds } : {}),
              ...(archIds.length ? { archNodeIds: archIds } : {}),
            })
          }
        >
          START PROJECT ▸
        </button>
        {queuedStartCount > 0 && (
          <span className="warn-text">
            ✓ {queuedStartCount} queued below — executes on END TURN
          </span>
        )}
      </div>
      {availableIp.length > 0 && (
        <div style={{ marginTop: 6, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <span className="hint">LICENSE IP (≤2):</span>
          {availableIp.map((ip) => {
            const on = ipIds.includes(ip.id);
            return (
              <label key={ip.id} className="hint" style={{ cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={on}
                  disabled={!on && ipIds.length >= 2}
                  onChange={() => setIpIds(on ? ipIds.filter((x) => x !== ip.id) : [...ipIds, ip.id])}
                />{" "}
                {ip.displayName} (−{Math.round(ip.kGatesFrac * 100)}% effort, {money(interpolate(ip.upfront, m))} +{" "}
                {(ip.royaltyPct * 100).toFixed(1)}% royalty{ip.lambdaMult > 1.05 ? ", ⚠ bug risk" : ""})
              </label>
            );
          })}
          {chosenIp.length > 0 && (
            <span className="warn-text">
              → effort runs on {num(kEff)}kG · {money(ipUpfront)} upfront · {(ipRoyalty * 100).toFixed(1)}% of revenue forever
            </span>
          )}
        </div>
      )}
      {tree && (
        <ArchitecturePicker
          snap={snap}
          content={content}
          session={session}
          researched={researched}
          excluded={excluded}
          setExcluded={setExcluded}
          archIds={archIds}
          marketId={marketId}
        />
      )}
      <div className="hint" style={{ marginTop: 6 }}>
        die {area.toFixed(0)}mm² · est. unit cost {uc.unitCost >= 1000 ? "∞ (unmanufacturable)" : `$${uc.unitCost.toFixed(2)}`} vs refASP $
        {refAspAt(market, mRel).toFixed(0)} at release · market expects ~{num(expected)}kG then · effort ≈ {plan.designEmTotal.toFixed(0)} design +{" "}
        {plan.implEmTotal.toFixed(0)} impl EM{p95Note(plan.verifScale)} · ~{Math.ceil(plan.blocks)} blocks → ideal team ≈{" "}
        {Math.ceil(models.designersPerBlock * plan.blocks)}D/{Math.ceil(models.implPerBlock * plan.blocks)}I/
        {Math.ceil(models.verifPerBlock * plan.blocks)}V (fewer = starved, more = diminishing returns)
      </div>
      {locksPath && (
        <div className="warn-text" style={{ marginTop: 4 }}>
          ⚠ FIRST CHIP HERE LOCKS THE COMPANY ONTO THE {market.pathAffinity.toUpperCase()} PATH — the defining choice (irreversible)
        </div>
      )}
      {offPath && (
        <div className="warn-text" style={{ marginTop: 4 }}>
          ⚠ OFF-PATH: you are a {view.path} company — this chip costs {((models.offPathEffortMult - 1) * 100).toFixed(0)}% more effort and
          reads requirements at {pct(models.offPathFitMult, 0)}
        </div>
      )}
    </div>
  );
}

function p95Note(verifScale: number): string {
  // coverage 0.9 costs -ln(0.1)×scale ≈ 2.3× scale
  return ` · 90% coverage ≈ ${(2.303 * verifScale).toFixed(0)} verif EM`;
}

/** Architecture picker (P1, spec §11): which researched tree nodes this chip is
 *  BUILT ON — snapshotted with their grades at start; the world keeps moving.
 *  The preview prices the set against every segment's live archFit. */
function ArchitecturePicker({
  snap,
  content,
  session: _session,
  researched,
  excluded,
  setExcluded,
  archIds,
  marketId,
}: ScreenProps & {
  researched: { id: string; displayName: string; exclusiveGroup?: string }[];
  excluded: string[];
  setExcluded: (x: string[]) => void;
  archIds: string[];
  marketId: string;
}) {
  const { view } = snap;
  const m = view.monthIndex;
  const ctx = viewCtx(view, content);
  const fake = { archNodes: archIds.map((id) => ({ id, grade: view.tree.researched[id]!.grade })) };
  const segments = content.markets.filter((mk) => mk.attrWeights);
  const selected = content.markets.find((mk) => mk.id === marketId)!;
  const gateBlocked = !!selected.techGatesAnyOf && !selected.techGatesAnyOf.some((id) => id in view.tree.researched);

  if (researched.length === 0) {
    return (
      <div className="hint" style={{ marginTop: 6 }}>
        ARCHITECTURE: nothing researched yet — a chip with no architecture ships blind into attr-weighted segments (archFit floor). The TREE
        screen is where chips come from now.
      </div>
    );
  }
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <span className="hint">ARCHITECTURE (researched nodes, grade-scaled):</span>
        {researched.map((n) => {
          const on = !excluded.includes(n.id);
          const grade = view.tree.researched[n.id]!.grade;
          return (
            <label key={n.id} className="hint" style={{ cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={on}
                onChange={() => setExcluded(on ? [...excluded, n.id] : excluded.filter((x) => x !== n.id))}
              />{" "}
              {n.displayName} <span className={`badge ${grade === "A" ? "cyan" : grade === "B" ? "dim" : "amber"}`}>{grade}</span>
            </label>
          );
        })}
      </div>
      {segments.length > 0 && (
        <div className="warn-text" style={{ marginTop: 4 }}>
          this set today:{" "}
          {segments
            .map((mk) => `${mk.displayName.split("—").pop()!.trim()} ×${archFitFactor(ctx, mk, fake, m).toFixed(2)}`)
            .join(" · ")}
          <span className="dim"> — recomputed monthly against the moving frontier; shipped bets age</span>
        </div>
      )}
      {gateBlocked && (
        <div className="bad-text" style={{ marginTop: 4 }}>
          ⚠ {selected.displayName} is TECH-GATED — research{" "}
          {selected.techGatesAnyOf!
            .map((id) => activeTreeOf(content)?.nodes.find((n) => n.id === id)?.displayName ?? id)
            .join(" or ")}{" "}
          first (the order will be refused)
        </div>
      )}
    </div>
  );
}
